import { applyStoryEngineMetaToStore, isCurrentStoryBibleOwner } from "src/utils/storyBibleUpdateFromChunk";
import * as Sentry from "@sentry/react";
import Auth from "src/Auth";

export type StoryBibleRefreshHint = {
  characters?: boolean;
  worldbuilding?: boolean;
  meta?: boolean;
  outline?: boolean;
  characterIds?: string[];
  worldbuildingIds?: string[];
  deletedCharacterIds?: string[];
  deletedWorldbuildingIds?: string[];
  characterFieldPathsById?: Record<string, string[]>;
  worldbuildingFieldPathsById?: Record<string, string[]>;
  metaFieldPaths?: string[];
};

export type AgenticChatRevertResponse = {
  restoredEntities?: string[];
  errors?: string[];
  restoredFieldPaths?: {
    character?: Record<string, string[]>;
    worldbuilding?: Record<string, string[]>;
    meta?: string[];
  };
};

export function parseAgenticChatRevertResponse(response: unknown): {
  restoredEntities: string[];
  errors: string[];
  restoredFieldPaths: {
    character: Record<string, string[]>;
    worldbuilding: Record<string, string[]>;
    meta: string[];
  };
} {
  const body = response as AgenticChatRevertResponse | null | undefined;
  const restoredEntities = Array.isArray(body?.restoredEntities)
    ? body.restoredEntities.filter((entity): entity is string => typeof entity === "string")
    : [];
  const errors = Array.isArray(body?.errors)
    ? body.errors.filter((message): message is string => typeof message === "string")
    : [];
  const sanitizeFieldMap = (value: unknown): Record<string, string[]> => {
    if (!value || typeof value !== "object" || Array.isArray(value)) return {};
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([id, paths]) => [
        id,
        Array.isArray(paths) ? paths.filter((path): path is string => typeof path === "string" && path.trim().length > 0) : [],
      ]),
    );
  };
  const restoredFieldPaths = {
    character: sanitizeFieldMap(body?.restoredFieldPaths?.character),
    worldbuilding: sanitizeFieldMap(body?.restoredFieldPaths?.worldbuilding),
    meta: Array.isArray(body?.restoredFieldPaths?.meta)
      ? body.restoredFieldPaths.meta.filter((path): path is string => typeof path === "string" && path.trim().length > 0)
      : [],
  };
  return { restoredEntities, errors, restoredFieldPaths };
}

/** User-facing toast after /revert returns HTTP 200 (chat may still be trimmed). */
export function agenticChatRevertOutcomeToast(
  restoredEntities: string[],
  errors: string[],
): string {
  if (errors.length === 0) {
    return "Reverted agent changes. Open History on any Story Bible card for older versions.";
  }
  if (restoredEntities.length > 0) {
    return "Reverted some Story Bible changes, but not all. Open History on the affected cards.";
  }
  return "Could not revert Story Bible changes. The chat was rolled back — use History on each card to restore older versions.";
}

export function storyBibleRefreshHintFromRestoredEntities(
  restoredEntities: string[],
  restoredFieldPaths?: {
    character?: Record<string, string[]>;
    worldbuilding?: Record<string, string[]>;
    meta?: string[];
  },
): StoryBibleRefreshHint {
  const characterIds = restoredEntities
    .filter((entity) => entity.startsWith("character:"))
    .map((entity) => entity.slice("character:".length))
    .filter(Boolean);
  const worldbuildingIds = restoredEntities
    .filter((entity) => entity.startsWith("worldbuilding:"))
    .map((entity) => entity.slice("worldbuilding:".length))
    .filter(Boolean);
  const deletedCharacterIds = restoredEntities
    .filter((entity) => entity.startsWith("deleted-created-character:"))
    .map((entity) => entity.slice("deleted-created-character:".length))
    .filter(Boolean);
  const deletedWorldbuildingIds = restoredEntities
    .filter((entity) => entity.startsWith("deleted-created-worldbuilding:"))
    .map((entity) => entity.slice("deleted-created-worldbuilding:".length))
    .filter(Boolean);
  return {
    outline: restoredEntities.includes("outline"),
    characters: restoredEntities.some(
      (entity) => entity.startsWith("character:") || entity.startsWith("deleted-created-character:"),
    ),
    worldbuilding: restoredEntities.some(
      (entity) => entity.startsWith("worldbuilding:") || entity.startsWith("deleted-created-worldbuilding:"),
    ),
    meta: restoredEntities.includes("meta"),
    characterIds,
    worldbuildingIds,
    deletedCharacterIds,
    deletedWorldbuildingIds,
    characterFieldPathsById: restoredFieldPaths?.character ?? {},
    worldbuildingFieldPathsById: restoredFieldPaths?.worldbuilding ?? {},
    metaFieldPaths: restoredFieldPaths?.meta ?? [],
  };
}

function normalizeDiffFieldPathCandidates(path: string): string[] {
  const trimmed = path.trim();
  if (!trimmed) return [];

  let unscoped = trimmed;
  while (unscoped.startsWith("fields.")) {
    unscoped = unscoped.slice("fields.".length);
  }

  const candidates = new Set<string>([unscoped]);

  if (unscoped.startsWith("customFields.")) {
    candidates.add(`custom.${unscoped.slice("customFields.".length)}`);
  }

  // Legacy trait updates can emit bare labels (e.g. "Motivation") while
  // Story Bible diff keys are flattened as `custom.<label>`.
  if (!unscoped.includes(".")) {
    candidates.add(`custom.${unscoped}`);
  }

  return [...candidates].filter(Boolean);
}

function resolveDiffFieldPathKeys(rawPath: string, availableKeys: Set<string>): string[] {
  const candidates = normalizeDiffFieldPathCandidates(rawPath);
  if (candidates.length === 0) return [];
  const matching = candidates.filter((candidate) => availableKeys.has(candidate));
  return matching.length > 0 ? matching : [candidates[0]];
}

/** Refresh Story Bible sidebar state after a successful /revert (matches stream storyBibleRefresh hints). */
export async function applyStoryBibleRefreshAfterRevert(
  projectId: string,
  hint: StoryBibleRefreshHint,
): Promise<void> {
  const { useStoryBibleStore } = await import("src/stores/storyBibleStore");
  const store = useStoryBibleStore.getState();
  if (!isCurrentStoryBibleOwner(store, projectId)) return;

  try {
    if (hint.outline) {
      const { useStructuredOutlineStore } = await import("src/components/StoryBible/StructuredOutline/StructuredOutlineStore");
      const outlineStore = useStructuredOutlineStore.getState();
      const outlineResponse = await Auth.fetchWithIdToken("/api/outline", { projectId });
      if (outlineResponse?.outline) {
        outlineStore.setOutline(outlineResponse.outline);
      }
      outlineStore.clearAllChapterDiffs();
    }

    if (hint.characters) {
      for (const characterId of hint.characterIds ?? []) {
        const fieldPaths = hint.characterFieldPathsById?.[characterId] ?? [];
        if (fieldPaths.length > 0) {
          const characterDiff = store.characterDiffs?.[characterId];
          const availableKeys = new Set<string>([
            ...Object.keys(characterDiff?.oldFields ?? {}),
            ...Object.keys(characterDiff?.newFields ?? {}),
          ]);
          for (const fieldPath of fieldPaths) {
            for (const fieldKey of resolveDiffFieldPathKeys(fieldPath, availableKeys)) {
              store.clearCharacterFieldDiff?.(characterId, fieldKey);
            }
          }
        } else {
          store.clearCharacterDiff?.(characterId);
        }
      }
      for (const characterId of hint.deletedCharacterIds ?? []) {
        store.clearCharacterDiff?.(characterId);
        store.removePendingNewCharacter?.(characterId);
      }
    }
    if (hint.worldbuilding) {
      for (const entryId of hint.worldbuildingIds ?? []) {
        const fieldPaths = hint.worldbuildingFieldPathsById?.[entryId] ?? [];
        if (fieldPaths.length > 0) {
          const worldbuildingDiff = store.worldbuildingDiffs?.[entryId];
          const availableKeys = new Set<string>([
            ...Object.keys(worldbuildingDiff?.oldFields ?? {}),
            ...Object.keys(worldbuildingDiff?.newFields ?? {}),
          ]);
          for (const fieldPath of fieldPaths) {
            for (const fieldKey of resolveDiffFieldPathKeys(fieldPath, availableKeys)) {
              store.clearWorldbuildingFieldDiff?.(entryId, fieldKey);
            }
          }
        } else {
          store.clearWorldbuildingDiff?.(entryId);
        }
      }
      for (const entryId of hint.deletedWorldbuildingIds ?? []) {
        store.clearWorldbuildingDiff?.(entryId);
        store.removePendingNewWorldbuilding?.(entryId);
      }
    }

    const refreshTasks: Promise<unknown>[] = [];
    if (hint.characters) refreshTasks.push(store.refreshCharacters(projectId, { force: true }));
    if (hint.worldbuilding) {
      refreshTasks.push(store.refreshWorldbuildingEntries(projectId, "storyBibleRefreshAfterRevert", { force: true }));
    }
    if (hint.meta) {
      if ((hint.metaFieldPaths?.length ?? 0) > 0) {
        for (const fieldPath of hint.metaFieldPaths ?? []) {
          store.clearMetaDiff?.(fieldPath);
        }
      } else {
        store.clearAllMetaDiffs();
      }
      refreshTasks.push(
        Auth.fetchWithIdToken(`/api/story-engine/${projectId}/get`, {}).then((results) => {
          applyStoryEngineMetaToStore(store, projectId, results);
        }),
      );
    }

    await Promise.all(refreshTasks);
  } catch (error) {
    Sentry.captureException(error, {
      tags: { context: "storyBibleRefreshAfterRevert" },
      extra: { projectId, hint },
    });
  }
}
